import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getClientCoursesAsync,
  getCourseProgressAsync,
} from "../../Services/CourseService";
import {
  getClientByUrlTokenAsync,
  getClientCourseProgressAsync,
  getClientCoursesDropdownAsync,
} from "../../Services/AdminService";
import { sendReminderAsync } from "../../Services/AccountService";
import Card from "../../Shared/Components/Card/Card";
import Header from "../../Shared/Components/Header/Header";
import Table from "../../Shared/Components/Table";
import {
  getStatus,
  getStatusBadge,
} from "./../../Infrastructure/Utilities/LearnerProgressHelper";
import PaginationControls from "../../Shared/Components/PaginationControls";
import Button from "../../Shared/Components/Button/Button";
import { showToast } from "../../Infrastructure/Notifications/ToastrConfig";
import { formatDateTime } from "../../Infrastructure/Utilities/DateHelper";

const sortCoursesByTitle = (courseList) =>
  [...courseList].sort((a, b) =>
    (a?.title || a?.name || "").localeCompare(b?.title || b?.name || ""),
  );

const LearnerProgress = () => {
  const { urlToken } = useParams();
  const [course, setCourse] = useState({});
  const [hasLearnerProgress, setHasLearnerProgress] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [courseId, setCourseId] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  const getCourses = useCallback(async () => {
    setIsLoaded(false);

    try {
      let availableCourses = [];

      if (urlToken) {
        const clientResponse = await getClientByUrlTokenAsync(urlToken);
        const resolvedClientId = clientResponse?.id || null;

        setSelectedClientId(resolvedClientId);
        setSelectedClientName(clientResponse?.name || "");

        if (resolvedClientId) {
          const dropdownResponse =
            await getClientCoursesDropdownAsync(resolvedClientId);
          availableCourses = Array.isArray(dropdownResponse?.courses)
            ? dropdownResponse.courses
            : Array.isArray(dropdownResponse)
              ? dropdownResponse
              : [];
        }
      } else {
        setSelectedClientId(null);
        setSelectedClientName("");
        const response = await getClientCoursesAsync();
        availableCourses = Array.isArray(response?.courses)
          ? response.courses
          : [];
      }

      const sortedCourses = sortCoursesByTitle(availableCourses);
      setCourses(sortedCourses);

      if (sortedCourses.length > 0) {
        setCourseId(sortedCourses[0].id);
      } else {
        setCourseId(null);
        setCourse({});
        setHasLearnerProgress(false);
      }
    } catch (error) {
      console.error("Failed to load courses for learner progress", error);
      setCourses([]);
      setCourseId(null);
      setCourse({});
      setHasLearnerProgress(false);
    } finally {
      setIsLoaded(true);
    }
  }, [urlToken]);

  useEffect(() => {
    getCourses();
  }, [getCourses]);

  useEffect(() => {
    const getCourse = async () => {
      if (courseId === null || (urlToken && !selectedClientId)) {
        return;
      }

      const response = selectedClientId
        ? await getClientCourseProgressAsync(
            selectedClientId,
            courseId,
            pageNumber,
          )
        : await getCourseProgressAsync(courseId, null, pageNumber);

      setCourse(response || {});
      setTotalPages(response?.totalPages || 0);
    };

    if (courses.length > 0 && courseId !== null) {
      getCourse();
    }
  }, [courseId, courses, pageNumber, selectedClientId, urlToken]);

  useEffect(() => {
    if (course.courses !== undefined) {
      setIsLoaded(true);
      setHasLearnerProgress(true);
    }
    setIsLoaded(true);
  }, [course]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [courseId, pageNumber]);

  const getLearnerId = (progressRow) => {
    return (
      progressRow?.learner?.id ||
      progressRow?.learnerId ||
      progressRow?.userId ||
      null
    );
  };

  const isIncompleteProgress = (progressRow) =>
    progressRow?.completedDateTime === null;

  const incompleteUserIds = (course?.courses || [])
    .filter(isIncompleteProgress)
    .map(getLearnerId)
    .filter(Boolean);

  const allIncompleteSelected =
    incompleteUserIds.length > 0 &&
    incompleteUserIds.every((userId) => selectedUserIds.includes(userId));

  const handleToggleUserSelection = (userId) => {
    if (!userId) {
      return;
    }

    setSelectedUserIds((previous) => {
      if (previous.includes(userId)) {
        return previous.filter((selectedId) => selectedId !== userId);
      }

      return [...previous, userId];
    });
  };

  const handleToggleAllIncomplete = () => {
    setSelectedUserIds((previous) => {
      if (allIncompleteSelected) {
        return previous.filter((userId) => !incompleteUserIds.includes(userId));
      }

      const merged = new Set([...previous, ...incompleteUserIds]);
      return Array.from(merged);
    });
  };

  const handleSendReminders = async () => {
    if (selectedUserIds.length === 0) {
      showToast(false, "Select at least one learner with incomplete progress");
      return;
    }

    setIsSendingReminders(true);
    try {
      const response = await sendReminderAsync({ userIds: selectedUserIds });
      if (response?.success) {
        showToast(
          true,
          `Reminder email sent to ${selectedUserIds.length} learner${selectedUserIds.length > 1 ? "s" : ""}`,
        );
        setSelectedUserIds([]);
      } else {
        showToast(
          false,
          response?.errors?.[0]?.error || "Failed to send reminder email(s)",
        );
      }
    } catch (error) {
      showToast(false, "Failed to send reminder email(s)");
    } finally {
      setIsSendingReminders(false);
    }
  };

  const updateCourseId = (evt) => {
    setPageNumber(1);
    setCourseId(evt.target.value);
  };

  const nextPage = () => {
    setPageNumber(pageNumber + 1);
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const selectedCourseHasAssessment =
    course?.hasAssessment !== undefined && course?.hasAssessment !== null
      ? !!course.hasAssessment
      : !!courses.find((c) => c.id === courseId)?.hasAssessment;

  const headerHeading = course.title
    ? course.title
    : selectedClientName
      ? `${selectedClientName} Learner Progress`
      : "Learner Progress";

  const headerSubheading = selectedClientName
    ? `Current progress of learners for ${selectedClientName}`
    : "Current progress of learners";

  const feedbackLink = selectedClientId
    ? `/feedback-stats/${course.feedbackFormId}/${selectedClientId}`
    : `/feedback-stats/${course.feedbackFormId}`;

  const headings = () => {
    return (
      <>
        <th style={{ width: "40px" }}>
          <input
            type="checkbox"
            checked={allIncompleteSelected}
            onChange={handleToggleAllIncomplete}
            disabled={incompleteUserIds.length === 0 || isSendingReminders}
          />
        </th>
        <th>Name</th>
        <th>Group</th>
        <th>Registered</th>
        <th>Status</th>
        <th>Started</th>
        <th>Completed</th>
        {selectedCourseHasAssessment && <th>Score</th>}
        {selectedCourseHasAssessment && <th>Result</th>}
        <th>No. of Restarts</th>
        <th></th>
      </>
    );
  };

  const rowData = () => {
    return (
      <>
        {course.courses.map((c, i) => {
          const learnerId = getLearnerId(c);
          const canSendReminder = isIncompleteProgress(c) && !!learnerId;

          return (
            <tr key={i}>
              <td>
                {canSendReminder ? (
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(learnerId)}
                    onChange={() => handleToggleUserSelection(learnerId)}
                    disabled={isSendingReminders}
                  />
                ) : null}
              </td>
              <td>{c.learner.name}</td>
              <td>{c.learner.groupName || "-"}</td>
              <td>{formatDateTime(c.learner.registeredDateTime)}</td>
              <td>
                {getStatus(
                  c.completedDateTime,
                  c.startedDateTime,
                  selectedCourseHasAssessment,
                  c.scorePercentage,
                )}
              </td>
              <td>{formatDateTime(c.startedDateTime)}</td>
              <td>{formatDateTime(c.completedDateTime)}</td>
              {selectedCourseHasAssessment && (
                <td>
                  {c.scorePercentage !== null && c.scorePercentage !== undefined
                    ? `${c.scorePercentage}%`
                    : "-"}
                </td>
              )}
              {selectedCourseHasAssessment && (
                <td>{getStatusBadge(c.lessonStatus, c.scorePercentage)}</td>
              )}
              <td>{c.numberOfRestarts}</td>
              <td>
                <Link
                  to={`/learner/${c.learner.urlToken}`}
                  className="btn btn-sm btn-outline-primary action-button float-right mr-3"
                >
                  View
                </Link>
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Header
        heading={headerHeading}
        subheading={headerSubheading}
        icon="stats-up"
      />
      <div className="container-fluid content">
        <div className="row">
          <div className="col-12">
            {isLoaded && hasLearnerProgress && (
              <Card classes="mb-5 course-item">
                <div className="form-group float-left">
                  <select
                    value={courseId || ""}
                    onChange={updateCourseId}
                    name="courseId"
                    className="form-control"
                  >
                    <option value="00000000-0000-0000-0000-000000000000">
                      Select course...
                    </option>
                    {courses.map((d, i) => (
                      <option key={i} value={d.id}>
                        {d.title}
                      </option>
                    ))}
                  </select>
                </div>
                {course.feedbackFormId !== null &&
                  course.feedbackFormId !== undefined && (
                    <div className="form-group float-left pl-5">
                      <Button
                        link={feedbackLink}
                        text="View feedback"
                        classes={"btn btn-primary"}
                      />
                    </div>
                  )}
                <div className="form-group float-right">
                  <strong>
                    <p>
                      <span style={{ color: "red" }}>
                        Not started: {course.stats.notStarted}
                      </span>{" "}
                      -{" "}
                      <span style={{ color: "Orange" }}>
                        In progress: {course.stats.inProgress}
                      </span>{" "}
                      -{" "}
                      <span style={{ color: "green" }}>
                        {" "}
                        Completed: {course.stats.completed}
                      </span>
                    </p>
                  </strong>
                </div>
                {incompleteUserIds.length > 0 && (
                  <div className="form-group float-right mr-4">
                    <button
                      className="btn btn-warning"
                      onClick={handleSendReminders}
                      disabled={
                        selectedUserIds.length === 0 || isSendingReminders
                      }
                    >
                      {isSendingReminders
                        ? "Sending..."
                        : `Send Reminder${selectedUserIds.length > 1 ? "s" : ""}${selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ""}`}
                    </button>
                  </div>
                )}
                <Table headings={headings} rowData={rowData} />
                <PaginationControls
                  next={nextPage}
                  previous={previousPage}
                  total={totalPages}
                  current={pageNumber}
                />
              </Card>
            )}
            {isLoaded && !hasLearnerProgress && (
              <Card classes="text-center p-5">
                <p
                  style={{
                    fontWeight: "700",
                    textTransform: "uppercase",
                    fontSize: "0.88rem",
                  }}
                >
                  No Learner Progress to display
                </p>

                <span>
                  {selectedClientName
                    ? `No learner progress is available yet for ${selectedClientName}`
                    : "You have not yet been assigned any pathways or none of your learners have logged in"}
                </span>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnerProgress;
